import { Command, CommandItem, CommandList } from "@/components/ui/command";
import { cn } from "@/lib/cn";

import { useCallback, useMemo } from "react";
import Icon from "../Icon/Icon";
import type { MultiSelectOption, MultiSelectProps } from "./types";

/**
 * 複数選択可能なSelect
 *
 * @example
 * ```tsx
 * <MultiSelect
 *   options={options}
 *   value={selectedValues}
 *   onValueChange={handleValuesChange}
 * />
 * ```
 */
export default function MultiSelect<TOption extends MultiSelectOption>(
  props: MultiSelectProps<TOption>,
) {
  const selectedOptions = useMemo(() => new Set(props.value), [props.value]);

  // オプションが選択された時の処理
  const onSelect = useCallback(
    (value: string): void => {
      props.onValueChange(
        selectedOptions.has(value)
          ? props.value.filter((v: string) => v !== value)
          : [...props.value, value],
      );
    },
    [props, selectedOptions],
  );

  return (
    <Command shouldFilter={false}>
      <CommandList className={cn("gap-1 p-1")}>
        {props.options.map((option: TOption) => (
          <CommandItem
            key={option?.key || option.value}
            value={option.value}
            onSelect={option.disabled || props.disabled ? undefined : onSelect}
            className={cn(
              "cursor-pointer gap-2",
              (option.disabled || props.disabled) &&
                "cursor-not-allowed opacity-50",
            )}
          >
            <div className="line-clamp-1">{option.label}</div>
            {selectedOptions.has(option.value) && (
              <Icon iconName="Check" className="absolute right-2" />
            )}
          </CommandItem>
        ))}
      </CommandList>
    </Command>
  );
}
